import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TOOLS, TOOL_BY_SLUG, PRICING_TIERS } from "@/lib/toolsContent";
import ToolMock from "@/components/marketing/ToolMock";
import DemoBanner from "@/components/marketing/DemoBanner";

function ToolHero({ tool }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(50rem 26rem at 80% 0%, ${tool.glow}, transparent 65%)` }} />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 md:pt-20 pb-14 relative grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <Link to="/#tools" className="inline-flex items-center gap-1 text-xs mb-6 hover:underline" style={{ color: "var(--muted)" }} data-testid="tool-back-link"><ArrowLeft size={12} /> All tools</Link>
          <div className="text-[0.6rem] uppercase tracking-[0.24em]" style={{ color: tool.accent }}>{tool.brand} · {tool.kicker}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.04em] leading-[0.98] mt-4 home-rise" style={{ color: "var(--ink)" }} data-testid="tool-page-title">{tool.headline}</h1>
          <p className="mt-5 text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "var(--ink-2)" }}>{tool.pitch}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/demo" className="btn-primary" data-testid="tool-demo-btn">Request a demo <ArrowRight size={14} /></Link>
            <Link to={tool.appLink} className="btn-ghost" data-testid="tool-signin-btn">Already a customer? Sign in</Link>
          </div>
        </div>
        <div className="lg:col-span-6"><ToolMock tool={tool} /></div>
      </div>
    </section>
  );
}

function Features({ tool }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-14 md:py-20" data-testid="tool-features">
      <h2 className="font-serif-display text-3xl md:text-4xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>What you get</h2>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tool.features.map(({ icon: Icon, title, body }) => (
          <div key={title} className="p-6 rounded-lg transition-transform duration-200 hover:-translate-y-0.5" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: tool.glow, color: tool.accent }}><Icon size={18} /></span>
            <h3 className="font-serif-display text-xl mt-4" style={{ color: "var(--ink)" }}>{title}</h3>
            <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--ink-2)" }}>{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Steps({ tool }) {
  return (
    <section style={{ background: "var(--bg-deep)", borderTop: "1px solid var(--border-subtle)", borderBottom: "1px solid var(--border-subtle)" }} data-testid="tool-steps">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 md:py-20">
        <h2 className="font-serif-display text-3xl md:text-4xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>How it works</h2>
        <ol className="mt-8 grid md:grid-cols-3 gap-6">
          {tool.steps.map((s, i) => (
            <li key={s.title} className="flex gap-4">
              <span className="font-serif-display text-5xl leading-none" style={{ color: tool.accent }}>{i + 1}</span>
              <span>
                <span className="block font-medium" style={{ color: "var(--ink)" }}>{s.title}</span>
                <span className="block text-sm mt-1" style={{ color: "var(--ink-2)" }}>{s.body}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function ToolPricing({ tool }) {
  if (tool.free) {
    return (
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-14 md:py-20" data-testid="tool-pricing">
        <h2 className="font-serif-display text-3xl md:text-4xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>Pricing</h2>
        <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-2xl">
          {[["Everyone", "$0", "Every feature: post, photos, articles, react, comment"], ["Supporter (optional)", "$4.99", "Help keep the platform running"]].map(([l, pr, d]) => (
            <div key={l} className="p-5 rounded-lg" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}>
              <div className="text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>{l}</div>
              <div className="font-serif-display text-3xl mt-2" style={{ color: tool.accent }}>{pr}<span className="text-sm" style={{ color: "var(--muted)" }}> /mo</span></div>
              <div className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>{d}</div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm" style={{ color: "var(--ink-2)" }}>Free for every church, with nothing to switch on.</p>
      </section>
    );
  }
  const isBase = tool.slug === "tracker";
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-14 md:py-20" data-testid="tool-pricing">
      <h2 className="font-serif-display text-3xl md:text-4xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>Pricing</h2>
      <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }}>
        {isBase ? "Every church starts here. Your price depends on how many people are in your directory." : `${tool.name} is an add-on to the Tracker. Its price follows your Tracker tier.`}
      </p>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {PRICING_TIERS.map((t) => (
          <div key={t.label} className="p-5 rounded-lg" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}>
            <div className="text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>{t.label}</div>
            <div className="font-serif-display text-3xl mt-2" style={{ color: tool.accent }}>{isBase ? `$${t.tracker}` : `+$${t.addon}`}<span className="text-sm" style={{ color: "var(--muted)" }}> /mo</span></div>
            <div className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>{t.range}</div>
          </div>
        ))}
      </div>
      <Link to="/pricing" className="inline-flex items-center gap-1 text-sm mt-5 hover:underline" style={{ color: "var(--gold)" }} data-testid="tool-pricing-link">Full pricing details <ArrowRight size={13} /></Link>
    </section>
  );
}

function Faq({ tool }) {
  return (
    <section className="max-w-4xl mx-auto px-6 lg:px-12 py-6" data-testid="tool-faq">
      <h2 className="font-serif-display text-3xl md:text-4xl tracking-[-0.03em] mb-4" style={{ color: "var(--ink)" }}>Questions</h2>
      <Accordion type="single" collapsible>
        {tool.faq.map((f, i) => (
          <AccordionItem key={f.q} value={`q${i}`} style={{ borderColor: "var(--border-subtle)" }}>
            <AccordionTrigger className="text-left" style={{ color: "var(--ink)" }} data-testid={`tool-faq-q-${i}`}>{f.q}</AccordionTrigger>
            <AccordionContent style={{ color: "var(--ink-2)" }}>{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function OtherTools({ tool }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-14" data-testid="tool-others">
      <div className="overline mb-4" style={{ color: "var(--muted)" }}>Works even better with</div>
      <div className="grid sm:grid-cols-3 gap-3">
        {TOOLS.filter((t) => t.slug !== tool.slug).map((t) => (
          <Link key={t.slug} to={`/tools/${t.slug}`} className="group p-5 rounded-lg flex items-center justify-between" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid={`tool-other-${t.slug}`}>
            <span>
              <span className="block text-[0.55rem] uppercase tracking-[0.2em]" style={{ color: t.accent }}>{t.kicker}</span>
              <span className="block font-serif-display text-lg" style={{ color: "var(--ink)" }}>{t.name}</span>
            </span>
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" style={{ color: t.accent }} />
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function ToolPage() {
  const { slug } = useParams();
  const tool = TOOL_BY_SLUG[slug];
  if (!tool) return <Navigate to="/" replace />;
  return (
    <div data-testid={`tool-page-${tool.slug}`}>
      <ToolHero tool={tool} />
      <Features tool={tool} />
      <Steps tool={tool} />
      <ToolPricing tool={tool} />
      <Faq tool={tool} />
      <OtherTools tool={tool} />
      <DemoBanner title={`Ready to bring ${tool.name} to your church?`} />
    </div>
  );
}
